import styled from '@emotion/styled';
import Button from '@shared/components/core/button/CoreButton';
import { CardStyle } from '@shared/components/core/card/LegacyCard';
import Icons from '@shared/components/core/images/Icons';
import { StickyFooter } from '@shared/components/core/structure/CommonBodyLayoutStyles';
import SrOnlyText from '@shared/components/core/text/SrOnlyText';
import { SubtitleText } from '@shared/components/core/text/Typography';
import UnsavedChangesDialog from '@shared/components/loading/UnsavedChangesDialog';
import { color } from '@shared/constants/Styles';
import { useEditProfileStore } from '@shared/hooks/state/EditProfileContainer';
import HotkeyModel from '@shared/models/deviceProfiles/HotkeyModel';
import { useTranslation } from 'react-i18next';

export default function ProfileHotkeysPageContent() {
    const { t } = useTranslation();
    const hotkeys = useEditProfileStore((state) => state.hotkeys);
    const isPreview = useEditProfileStore((state) => state.isPreview);
    const hasChanges = useEditProfileStore((state) => state.hasChanges);
    const id = useEditProfileStore((state) => state.id);
    const originatedByProfileId = useEditProfileStore((state) => state.originatedByProfileId);

    const moveHotkeyUp = (hotkey: HotkeyModel) => {
        const editProfileStore = useEditProfileStore.getState();
        const currentHotkeys = editProfileStore.hotkeys;
        const index = currentHotkeys.indexOf(hotkey);
        if (index == 0) return;
        const temp = currentHotkeys[index - 1];
        currentHotkeys[index - 1] = currentHotkeys[index];
        currentHotkeys[index] = temp;
        editProfileStore.updateHotkeys([...currentHotkeys]);
        editProfileStore.setHasChanges(true);
    };
    const moveHotkeyDown = (hotkey: HotkeyModel) => {
        const editProfileStore = useEditProfileStore.getState();
        const currentHotkeys = editProfileStore.hotkeys;
        const index = currentHotkeys.indexOf(hotkey);
        if (index == currentHotkeys.length - 1) return;
        const temp = currentHotkeys[index + 1];
        currentHotkeys[index + 1] = currentHotkeys[index];
        currentHotkeys[index] = temp;
        editProfileStore.updateHotkeys([...currentHotkeys]);
        editProfileStore.setHasChanges(true);
    };

    const handleSave = async () => {
        const result = await useEditProfileStore.getState().saveChanges();
    };
    return (
        <>
            {hotkeys?.length > 0 ? (
                <HotkeyWrapper>
                    <StyledSubtitleText>{t('common:hotkey.reorder')}</StyledSubtitleText>
                    {hotkeys?.map((h, i) => (
                        <Card key={i}>
                            <Content>
                                <Icons name="virtualButton" size={24} />
                                <h1>{h.displayName}</h1>
                            </Content>
                            <ButtonsContainer>
                                {i > 0 && (
                                    <StyledButton
                                        $color="simple"
                                        $size="small"
                                        disabled={isPreview}
                                        onClick={() => moveHotkeyUp(h)}
                                        startIcon="moveUp">
                                        <SrOnlyText>{t('common:event.options.moveUp')}</SrOnlyText>
                                    </StyledButton>
                                )}
                                {i < hotkeys.length - 1 && (
                                    <StyledButton
                                        $color="simple"
                                        $size="small"
                                        disabled={isPreview}
                                        onClick={() => moveHotkeyDown(h)}
                                        startIcon="moveDown">
                                        <SrOnlyText>{t('common:event.options.moveDown')}</SrOnlyText>
                                    </StyledButton>
                                )}
                            </ButtonsContainer>
                        </Card>
                    ))}
                </HotkeyWrapper>
            ) : (
                <HotkeyWrapper>
                    <Message>{t('common:hotkey.empty')}</Message>
                </HotkeyWrapper>
            )}
            {hasChanges && (
                <UnsavedChangesDialog
                    hasChanges={hasChanges}
                    setHasChanges={useEditProfileStore.getState().setHasChanges}
                    onSave={handleSave}
                />
            )}
            <StickyFooter
                contentPosition="center"
                $visuallyHide={!hasChanges || (id !== undefined && originatedByProfileId !== undefined)}>
                <Button $color="primary" $size="large" startIcon="save" onClick={handleSave}>
                    {t('common:saveAndFinish')}
                </Button>
            </StickyFooter>
        </>
    );
}

const Message = styled.div`
    min-height: 40vh;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
`;

const HotkeyWrapper = styled.div`
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 1rem;
    justify-content: center;
    margin: 1rem;
`;

const StyledButton = styled(Button)`
    gap: 0;
`;

const Card = styled(CardStyle)`
    padding: 0.5rem;
    width: 45%;
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    flex-grow: 1;
    justify-content: space-between;

    border: 1px solid ${(props) => props.theme.color.neutral};
    background: ${(props) => props.theme.color.accentCard};
    &:last-child:nth-of-type(odd) {
        width: 90%;
        height: 100px;
    }
`;

const ButtonsContainer = styled.div`
    align-self: center;
    flex: 0 0 auto;
    display: flex;
    flex-direction: column;
    flex-wrap: wrap;
    align-items: flex-end;
    justify-content: space-between;
    gap: 0.25rem;
`;

const Content = styled.div`
    flex: 1;
    align-items: center;
    justify-content: center;
    display: flex;
    flex-direction: row;
    gap: 0.5rem;
`;

const StyledSubtitleText = styled(SubtitleText)`
    padding: 1rem 0;
    width: 100%;
`;

export const EditWrapper = styled.div`
    border: solid 1px ${color.primary_dark}40;
    border-radius: 10px;
    margin: -2rem 0 1rem 0;
    padding: 1rem;
    width: 100%;
    > * {
        max-width: calc(100% - 2rem);
    }
`;
